// Export Workflow — upfront review scope (full review vs specific checks).
// Pure helpers + the pyapi scope-suggest call behind the landing page. The backend is authoritative
// (pyapi `_scope_run_set` / `_SCOPE_PREREQS` / `_accept_scope_choice`); these mirror it only so the
// landing page can show, before Start, exactly what will run.
import axios from 'axios';
import KeycloakService from 'services/KeycloakService';
import { ExportAssessmentPhase } from '../interfaces/iStreamedResponse';

export type ExportScopeMode = 'full' | 'specific';
/** Phases the user can pick. Product Profile is never selectable — it runs only in a full review.
 *  ("export_screening" is only an assessment key now — part of the Country and Control List Check.) */
export type ExportScopePhase = Exclude<ExportAssessmentPhase, 'profile' | 'export_screening'>;

export interface ExportScopeChoice {
  scope: ExportScopeMode;
  selectedPhases: ExportScopePhase[];
}

export interface ExportScopeSuggestion {
  scope: ExportScopeMode;
  selected_phases: ExportScopePhase[];
  run_set: ExportAssessmentPhase[];
  auto_included: Partial<Record<ExportAssessmentPhase, ExportAssessmentPhase[]>>;
  unclear: boolean;
  source: 'detector' | 'default';
}

export const EXPORT_SCOPE_PHASE_ORDER: ExportScopePhase[] = [
  'schedule_b',
  'country',
  'nrc',
  'itar',
  'ccl',
];

/** Check wording on the landing page and the right panel's "Required for …" note. */
export const EXPORT_SCOPE_CHIP_LABEL: Record<ExportScopePhase, string> = {
  schedule_b: 'Schedule B / HTS',
  country: 'Country and Control List Check',
  nrc: 'Nuclear (NRC + DOE)',
  itar: 'ITAR',
  ccl: 'EAR / CCL',
};

// Mirrors pyapi _SCOPE_PREREQS: a selected phase would auto-add these. Empty by design (Bug 5,
// 2026-10-06) — a specific run executes exactly the picked checks; the order-of-review advisory
// (ITAR / EAR → Nuclear first) is separate and still shown in the run.
export const EXPORT_SCOPE_PREREQS: Partial<Record<ExportScopePhase, ExportScopePhase[]>> = {};

export const DEFAULT_EXPORT_SCOPE_CHOICE: ExportScopeChoice = {
  scope: 'full',
  selectedPhases: [],
};

/** {prereq: [selected phases needing it]} for prerequisites the user didn't pick themselves. */
export function scopeAutoIncluded(
  selected: ExportScopePhase[],
): Partial<Record<ExportScopePhase, ExportScopePhase[]>> {
  const picked = new Set(selected);
  const out: Partial<Record<ExportScopePhase, ExportScopePhase[]>> = {};
  EXPORT_SCOPE_PHASE_ORDER.forEach((phase) => {
    if (!picked.has(phase)) return;
    (EXPORT_SCOPE_PREREQS[phase] || []).forEach((req) => {
      if (picked.has(req)) return;
      out[req] = [...(out[req] || []), phase];
    });
  });
  return out;
}

/** The `_control` fields Start sends (pyapi `_accept_scope_choice` normalises them). */
export function scopeControlFields(choice: ExportScopeChoice): Record<string, unknown> {
  const specific = choice.scope === 'specific' && choice.selectedPhases.length > 0;
  return {
    review_scope: specific ? 'specific' : 'full',
    selected_phases: specific ? choice.selectedPhases : [],
  };
}

/** Map the backend suggestion onto the landing page's choice (anything unusable → full). */
export function choiceFromSuggestion(s: ExportScopeSuggestion | null): ExportScopeChoice {
  const picked = (s?.selected_phases || []).filter((p) =>
    EXPORT_SCOPE_PHASE_ORDER.includes(p),
  );
  if (!s || s.scope !== 'specific' || !picked.length) return DEFAULT_EXPORT_SCOPE_CHOICE;
  return {
    scope: 'specific',
    selectedPhases: EXPORT_SCOPE_PHASE_ORDER.filter((p) => picked.includes(p)),
  };
}

// Instant client-side pre-highlight while the server detector is in flight. Deliberately narrow:
// it only reacts to a check NAMED explicitly together with a limiting cue ("just"/"only"/a direct
// question about that check); anything else stays on the full review (the safe direction). The
// server detector (intent-based) replaces this as soon as it answers.
const KEYWORD_PHASES: Array<[ExportScopePhase, RegExp]> = [
  ['ccl', /\b(eccn|ear99|ccl)\b/i],
  ['itar', /\b(itar|usml)\b/i],
  ['nrc', /\b(nrc|part 810|nuclear[- ]controlled)\b/i],
  ['schedule_b', /\b(schedule b|hts code)\b/i],
];
const LIMITING_CUE = /\b(just|only|what'?s the|what is the|is (this|it) .*(controlled|itar)|classify)\b/i;
const FULL_CUE = /\b(full|complete|comprehensive|everything|all (the )?checks)\b/i;

export function keywordScopeGuess(text: string): ExportScopeChoice | null {
  const t = String(text || '');
  if (!t.trim() || FULL_CUE.test(t) || !LIMITING_CUE.test(t)) return null;
  const hits = KEYWORD_PHASES.filter(([, re]) => re.test(t)).map(([p]) => p);
  if (!hits.length) return null;
  return {
    scope: 'specific',
    selectedPhases: EXPORT_SCOPE_PHASE_ORDER.filter((p) => hits.includes(p)),
  };
}

/** POST pyapi /export/workflow/scope-suggest. Suggestion only; null on any failure. */
export async function fetchScopeSuggestion(
  description: string,
  signal?: AbortSignal,
): Promise<ExportScopeSuggestion | null> {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_PYAPI_URL}/export/workflow/scope-suggest`,
      { description: description },
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${KeycloakService.getToken()}`,
        },
        timeout: 15000,
        signal: signal,
      },
    );
    return (res?.data as ExportScopeSuggestion) || null;
  } catch (_error) {
    return null;
  }
}
